import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Send, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/api/client";

// V1.1d — "Mail this letter" upgrade card embedded BELOW the existing
// Download / Save row on the dispute-letter detail page. Stays compact
// when the letter has never been mailed; expands into a status timeline
// once a `mailed_letters` row exists.

const STATUS_META = {
  submitted:              { label: "Submitted",          icon: Send,         color: "#FCD34D", bg: "rgba(252,211,77,0.10)", border: "rgba(252,211,77,0.30)" },
  in_transit:             { label: "In transit",          icon: Truck,        color: "#5D50C8", bg: "rgba(167,139,250,0.10)", border: "rgba(167,139,250,0.30)" },
  processed_for_delivery: { label: "At post office",      icon: Truck,        color: "#C4B5FD", bg: "rgba(196,181,253,0.10)", border: "rgba(196,181,253,0.30)" },
  delivered:              { label: "Delivered",           icon: CheckCircle2, color: "#86EFAC", bg: "rgba(134,239,172,0.10)", border: "rgba(134,239,172,0.30)" },
  returned:               { label: "Returned to sender",  icon: XCircle,      color: "#FCA5A5", bg: "rgba(252,165,165,0.10)", border: "rgba(252,165,165,0.30)" },
  canceled:               { label: "Canceled",            icon: XCircle,      color: "#FCA5A5", bg: "rgba(252,165,165,0.10)", border: "rgba(252,165,165,0.30)" },
};


export default function MailLetterCard({ letter, onMailed }) {
  const [busy, setBusy] = useState(false);
  const [mailed, setMailed] = useState(null); // hydrated `mailed_letters` row
  const [confirming, setConfirming] = useState(false);
  const hasMailed = !!letter.latest_mailed_letter_id || letter.mailing_status !== "not_mailed";

  useEffect(() => {
    if (!letter.latest_mailed_letter_id) return;
    let cancelled = false;
    api
      .get(`/mailed-letters/${letter.latest_mailed_letter_id}`)
      .then(({ data }) => !cancelled && setMailed(data))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [letter.latest_mailed_letter_id]);

  async function mail() {
    setBusy(true);
    try {
      const { data } = await api.post(`/dispute-letters/${letter.id}/mail`, {
        mail_type: "usps_first_class",
        color: false,
        double_sided: true,
      });
      setMailed(data);
      setConfirming(false);
      toast.success("Letter handed off to Lob (sandbox).");
      onMailed?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    if (!mailed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/mailed-letters/${mailed.id}/refresh-status`);
      setMailed(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  // --- Empty state: never mailed -------------------------------------------
  if (!hasMailed) {
    return (
      <section
        data-testid="mail-letter-card"
        className="mt-6 rounded-2xl p-5 md:p-6"
        style={{
          background: "linear-gradient(135deg, rgba(167,139,250,0.10) 0%, rgba(167,139,250,0.02) 100%)",
          border: "1px solid rgba(167,139,250,0.35)",
        }}
      >
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Send className="w-4 h-4 text-[var(--v2-accent)]" />
              <h3 className="font-display text-base text-[var(--v2-text)]">Mail this letter</h3>
              <span
                data-testid="mail-letter-card-sandbox-pill"
                className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
                style={{ background: "rgba(252,211,77,0.10)", color: "#FCD34D", border: "1px solid rgba(252,211,77,0.30)" }}
              >
                Sandbox · simulated
              </span>
            </div>
            <p className="text-sm text-[var(--v2-text-muted)] leading-relaxed">
              Skip the trip to the post office. We'll hand this letter to Lob
              (USPS First Class, double-sided, certified envelope) and track
              every status from submitted → delivered. We're currently in
              sandbox mode, so no real letter will be printed yet — your
              admin team will enable live mailing when keys are configured.
            </p>
            <p className="text-[11px] text-[var(--v2-text-muted)] mt-2">
              Included with your subscription · $1.65 fulfillment (USPS First Class)
            </p>
          </div>
          {!confirming ? (
            <button
              type="button"
              data-testid="mail-letter-card-cta"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, var(--v2-accent) 0%, #C4B5FD 100%)",
                color: "var(--v2-accent-ink)",
              }}
            >
              <Send className="w-3.5 h-3.5" /> Mail this letter
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                data-testid="mail-letter-card-cancel"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="px-3 py-2 rounded-full text-xs font-semibold text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
                style={{ border: "1px solid var(--v2-border)" }}
              >
                Not yet
              </button>
              <button
                type="button"
                data-testid="mail-letter-card-confirm"
                onClick={mail}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                style={{
                  background: "linear-gradient(135deg, var(--v2-accent) 0%, #C4B5FD 100%)",
                  color: "var(--v2-accent-ink)",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? "Submitting…" : "Confirm & mail"}
              </button>
            </div>
          )}
        </div>
        {confirming && (
          <p className="mt-3 text-xs text-[var(--v2-text-muted)]">
            By confirming, you authorize us to submit this letter for printing &amp; mailing on your behalf.
          </p>
        )}
      </section>
    );
  }

  // --- Mailed state: show status timeline ----------------------------------
  const status = mailed?.status || letter.mailing_status || "submitted";
  const meta = STATUS_META[status] || STATUS_META.submitted;
  const Icon = meta.icon;

  return (
    <section
      data-testid="mail-letter-card-mailed"
      className="mt-6 rounded-2xl p-5 md:p-6"
      style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="inline-flex w-8 h-8 items-center justify-center rounded-full"
          style={{ background: meta.bg, border: `1px solid ${meta.border}`, color: meta.color }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base text-[var(--v2-text)]">{meta.label}</h3>
            <span
              data-testid="mail-letter-card-mode"
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{
                background: (mailed?.lob_mode || "stub") === "live" ? "rgba(134,239,172,0.10)" : "rgba(252,211,77,0.10)",
                color: (mailed?.lob_mode || "stub") === "live" ? "#86EFAC" : "#FCD34D",
                border: `1px solid ${(mailed?.lob_mode || "stub") === "live" ? "rgba(134,239,172,0.30)" : "rgba(252,211,77,0.30)"}`,
              }}
            >
              {(mailed?.lob_mode || "stub") === "live" ? "Live" : "Sandbox"}
            </span>
          </div>
          <div className="text-[11px] text-[var(--v2-text-muted)] mt-0.5">
            {mailed?.lob_letter_id || letter.latest_mailed_letter_id}
            {mailed?.submitted_at && ` · sent ${new Date(mailed.submitted_at).toLocaleString()}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid="mail-letter-card-refresh"
            onClick={refresh}
            disabled={busy}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-[var(--v2-text)]"
            style={{ background: "var(--v2-surface-subtle)", border: "1px solid var(--v2-border)", opacity: busy ? 0.6 : 1 }}
          >
            Refresh
          </button>
          <Link
            to="/mailed-letters"
            data-testid="mail-letter-card-view-all"
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-[var(--v2-accent)] hover:text-white transition-colors"
            style={{ border: "1px solid rgba(167,139,250,0.35)" }}
          >
            View all
          </Link>
        </div>
      </div>

      {mailed?.tracking_events && mailed.tracking_events.length > 0 && (
        <ol
          data-testid="mail-letter-card-timeline"
          className="mt-4 space-y-2 relative pl-5 border-l border-[var(--v2-border)]"
        >
          {mailed.tracking_events.map((ev, i) => (
            <li key={i} className="relative">
              <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
              <div className="text-sm text-[var(--v2-text-muted)]">{ev.name}</div>
              <div className="text-[11px] text-[var(--v2-text-muted)]">
                {ev.time ? new Date(ev.time).toLocaleString() : ""}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
